<template>
  <div id="account">
    <div class="container">
      <div v-if="user">
        <h2>My Account</h2>
        <el-table :data="[1]" :highlight-current-row="false" stripe border>
          <el-table-column label="Email">
            <template slot-scope="scope">
              <span>{{ user.email }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Joined">
            <template slot-scope="scope">
              <span>{{ user.createdAt | toDate }}</span>
            </template>
          </el-table-column>
          <el-table-column label="License">
            <template slot-scope="scope">
              <span class="license">{{ license }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-if="subscriptions.length">
        <h2>Subscription</h2>
        <el-table :data="subscriptions" :highlight-current-row="false" stripe border>
          <el-table-column label="Plan">
            <template slot-scope="scope">
              <span>{{ scope.row.quantity === 3 ? 'Silver' : 'Gold' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Concurrent Pairs">
            <template slot-scope="scope">
              <span>{{ scope.row.quantity }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Status">
            <template slot-scope="scope">
              <span>{{ scope.row.status }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Price">
            <template slot-scope="scope">
              <span>{{ scope.row.price | currency }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Billing Interval">
            <template slot-scope="scope">
              <span>{{ prettyBillingInterval }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Actions">
            <template slot-scope="scope">
              <el-button v-if="scope.row.status === 'ACTIVE'" size="mini" @click="editSubscription(scope.row)">Change</el-button>
              <el-button v-if="scope.row.status === 'ACTIVE'" size="mini" type="danger" @click="cancelSubscription(scope.row)">Cancel</el-button>
              <el-button v-if="scope.row.status === 'CANCEL_PENDING'" size="mini" type="primary" @click="renewSubscription(scope.row)">Renew</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-if="activeJobs.length">
        <h2>Defending <el-tag>{{ activeJobs.length }}</el-tag></h2>
        <el-table :data="activeJobs" :highlight-current-row="false" stripe border>
          <el-table-column label="Pair">
            <template slot-scope="scope">
              <span>{{ scope.row.currency }}/{{ scope.row.baseCurrency }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Status">
            <template slot-scope="scope">
              <span>{{ scope.row.status }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Recovered">
            <template slot-scope="scope">
              <span>{{ scope.row.currentProfitAmount.toFixed(8) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Complete">
            <template slot-scope="scope">
              <span>{{ scope.row.percentComplete | toPercent }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Round">
            <template slot-scope="scope">
              <span>{{ scope.row.currentRound }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Updated At">
            <template slot-scope="scope">
              <span>{{ scope.row.statusUpdatedAt | toDateTime }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Actions">
            <template slot-scope="scope">
              <el-button size="mini" type="danger" @click="deleteJob(scope.row)">Delete</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-if="completeJobs.length">
        <h2>Complete <el-tag>{{ completeJobs.length }}</el-tag></h2>
        <el-table :data="completeJobs" :highlight-current-row="false" stripe border>
          <el-table-column label="Pair">
            <template slot-scope="scope">
              <span>{{ scope.row.currency }}/{{ scope.row.baseCurrency }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Status">
            <template slot-scope="scope">
              <span>{{ scope.row.status }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Recovered">
            <template slot-scope="scope">
              <span>{{ scope.row.currentProfitAmount.toFixed(8) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Round">
            <template slot-scope="scope">
              <span>{{ scope.row.currentRound }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Completed At">
            <template slot-scope="scope">
              <span>{{ scope.row.statusUpdatedAt | toDateTime }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import api from '@/lib/api'
import license from '@/lib/license'

export default {
  name: 'Account',
  data() {
    return {
      user: null,
      subscriptions: [],
      jobs: [],
      logs: []
    }
  },
  filters: {
    toPercent(val) {
      return `${parseInt(val * 100)}%`
    },
    toDate(val) {
      return DateTime.fromISO(val).toFormat('MMMM d, yyyy')
    },
    toDateTime(val) {
      return DateTime.fromISO(val).toFormat('yyyy/M/d h:mm a')
    },
    currency(val) {
      return '$' + parseFloat(val).toLocaleString('en', {
        useGrouping: true,
        minimumFractionDigits: 2
      })
    }
  },
  computed: {
    prettyBillingInterval() {
      let [subscription] = this.subscriptions
      if (!subscription) return 'None'
      if (subscription.price === 0) return 'Lifetime'
      return subscription.billingInterval === 1 ? 'Yearly' : 'Monthly'
    },
    activeJobs() {
      let activeStatus = new Set(['WAITING_FOR_BUY', 'WAITING_FOR_SELL', 'FINALIZE_BUY', 'FINALIZE_SELL', 'FINALIZE_COMPLETE'])
      return this.jobs.filter(job => activeStatus.has(job.status))
    },
    completeJobs() {
      let completeStatus = new Set(['COMPLETE'])
      return this.jobs.filter(job => completeStatus.has(job.status))
    },
    license() {
      return license.current()
    }
  },
  methods: {
    editSubscription(sub) {
      this.$router.push({ path: `/subscription/${sub.id}` })
    },
    async cancelSubscription(sub) {
      try {
        await this.$confirm('Are you sure you want to cancel your subscription? Once canceled, you will continue to have access to PTDefender until the end of your billing period.', 'Cancel Subscription', {
          confirmButtonText: 'Cancel Subscription',
          cancelButtonText: 'Keep Subscription'
        })
        await delay(800)
      } catch(err) {
        return
      }

      try {
        await this.$prompt('Please enter your PTDefender License to confirm:', 'Cancel Subscription', {
          confirmButtonText: 'Cancel Subscription',
          cancelButtonText: 'Keep Subscription',
          inputPattern: new RegExp(this.license),
          inputErrorMessage: 'Invalid License'
        })
      } catch(err) {
        return
      }

      let loading = this.$loading({ lock: true })

      try {
        await api.cancelSubscription(sub.id)
      } catch(err) {
        this.showError(err)
      }

      this.subscriptions = await api.listSubscriptions()

      loading.close()
    },
    async renewSubscription(sub) {
      let loading = this.$loading({ lock: true })

      try {
        await api.updateSubscription(sub.id, { renew: true })
      } catch(err) {
        this.showError(err)
      }

      this.subscriptions = await api.listSubscriptions()

      loading.close()
    },
    async deleteJob(job) {
      try {
        await this.$confirm('Are you sure you want to delete this pair? Only continue if you cannot stop defending this pair from the Defender app.', 'Delete Pair', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel'
        })
      } catch(err) {
        return
      }

      await api.deleteJob(job.id)

      this.jobs = this.jobs.filter(_job => _job.id !== job.id)
    },
    showError(err) {
      let title = err.response ? err.response.data.title : 'Error'
      let message = err.response ? err.response.data.message : err.message
      this.$message.error(`${title}: ${message}`)
    }
  },
  async mounted() {
    if (license.requireAuth(this)) return
    let [user, subscriptions, data] = await Promise.all([
      api.me(),
      api.listSubscriptions(),
      api.monitor()
    ])
    this.user = user
    this.subscriptions = subscriptions
    this.jobs = data.jobs
    this.logs = data.logs
  }
}

function delay(timeout) {
  return new Promise(resolve => {
    setTimeout(resolve, timeout)
  })
}
</script>

<style lang="scss">
#account {
  padding: 40px 0;
  min-height: 600px;

  h2 {
    margin-bottom: 10px;
  }

  .el-table {
    margin-bottom: 40px;
  }

  span.license {
    font-family: 'Courier New', Courier, monospace;
    font-weight: bold;
  }

  .el-tag {
    margin-left: 8px;
    height: 26px;
    line-height: 24px;
    font-size: 14px;
    transform: translate(0, -2px);
  }
}
</style>
